import QRCodeStyling from 'qr-code-styling';
import type { QRConfig } from '../store/useAppStore';
import { getQROptions, getFrameMetrics, getRoundedFramePathString } from './qr-helpers';
import { FONT_FAMILY } from './constants';

// Initialize QR Code generator
export const createQRGenerator = (width: number, content: string, config: QRConfig, logoFile?: string | null, forceTransparentBg = false) => {
  const options = getQROptions(width, content, config, logoFile, forceTransparentBg);
  return new QRCodeStyling(options);
};

// Compose frame onto a canvas
export const composeCanvasWithFrame = async (
  qr: QRCodeStyling,
  width: number,
  config: QRConfig
): Promise<HTMLCanvasElement> => {
  const rawQrBlob = await qr.getRawData('png');
  if (!rawQrBlob) throw new Error('Failed to render QR to Blob');
  
  const bitmap = await createImageBitmap(rawQrBlob as Blob);
  
  const { frameHeight, radius, fontSize } = getFrameMetrics(width, config.frameStyle);

  const compositeCanvas = document.createElement('canvas');
  compositeCanvas.width = width;
  compositeCanvas.height = width + frameHeight;
  
  const ctx = compositeCanvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get 2d context for composite canvas');

  // Fill background
  if (!config.backgroundTransparent) {
    ctx.fillStyle = config.backgroundColor;
    ctx.fillRect(0, 0, compositeCanvas.width, compositeCanvas.height);
  }

  // Draw QR perfectly centered by extracting its true bounding box
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = width;
  tempCanvas.height = width;
  const tCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
  if (tCtx) {
    tCtx.drawImage(bitmap, 0, 0, width, width);
    const imgData = tCtx.getImageData(0, 0, width, width).data;
    let minX = width, minY = width, maxX = 0, maxY = 0;
    
    // Find bounds in a single pass to optimize execution complexity and consolidate redundant logic
    for (let y = 0; y < width; y++) {
      for (let x = 0; x < width; x++) {
        if (imgData[(y * width + x) * 4 + 3] > 10) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    
    const box = minX > maxX ? { x: 0, y: 0, w: width, h: width } : { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
    
    const drawX = Math.round((width - box.w) / 2);
    const drawY = Math.round((width - box.h) / 2);
    
    ctx.drawImage(tempCanvas, box.x, box.y, box.w, box.h, drawX, drawY, box.w, box.h);
  } else {
    // Fallback if tempCanvas context fails (extremely rare)
    ctx.drawImage(bitmap, 0, 0, width, width);
  }

  // Draw frame
  if (config.frameStyle !== 'none') {
    ctx.fillStyle = config.frameColor;
    const y = width;
    
    if (config.frameStyle === 'rounded') {
      const pathString = getRoundedFramePathString(width, frameHeight, radius);
      const path = new Path2D(pathString);
      ctx.fill(path);
    } else {
      ctx.fillRect(0, y, width, frameHeight);
    }

    if (config.frameLabel) {
      ctx.fillStyle = config.frameLabelColor;
      ctx.font = `600 ${fontSize}px ${FONT_FAMILY}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      let labelText = config.frameLabel;
      // Truncate logic if exceeds width
      let textWidth = ctx.measureText(labelText).width;
      const maxWidth = width - 16; // 8px padding each side
      if (textWidth > maxWidth) {
        while (textWidth > maxWidth && labelText.length > 0) {
          labelText = labelText.slice(0, -1);
          textWidth = ctx.measureText(labelText + '…').width;
        }
        labelText += '…';
      }
      
      ctx.fillText(labelText, width / 2, y + frameHeight / 2);
    }
  }

  return compositeCanvas;
};
